import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { t } from "@/languages/i18n"
import useIsMobile from "@/lib/config/lib/use-is-mobile"
import { DefaultImage } from "@/lib/external-components"
import { ComponentConfig } from "@measured/puck"
import { Modal } from "antd"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import { Autoplay, Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import "./style.css"

export interface CardProps {
  img?: string | { url: string; alternativeText?: string }
  name?: string
  position?: string
  description?: string
  link?: string
}

export interface CardProfile extends CommonStylesProps {
  mailTo?: string
  items: CardProps[]
}

export const RenderConfig: ComponentConfig<CardProfile> = {
  render: ({ items, mailTo }) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState<CardProps | null>(null)
    const contentRef = useRef<HTMLDivElement | null>(null)
    const [shouldScroll, setShouldScroll] = useState(false)
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(420)
    const isMobile = useIsMobile()
    const rafIdRef = useRef<number | null>(null)
    const cleanupRef = useRef<(() => void) | null>(null)

    const handleItemClick = (item: CardProps) => {
      setSelectedItem(item)
      setIsModalOpen(true)
    }

    const handleModalClose = () => {
      setIsModalOpen(false)
      setSelectedItem(null)
      setShouldScroll(false)
    }

    useEffect(() => {
      if (!isModalOpen || !selectedItem) return

      // Cleanup trước đó nếu có
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }

      // Đợi DOM được cập nhật trước khi truy cập ref
      const checkRef = () => {
        const el = contentRef.current
        if (!el) {
          // Nếu ref vẫn null sau một frame, thử lại một lần nữa
          rafIdRef.current = requestAnimationFrame(() => {
            const retryEl = contentRef.current
            if (retryEl) {
              // Reset scroll về đầu
              retryEl.scrollTop = 0
              const update = () => {
                const currentEl = contentRef.current
                if (currentEl) {
                  setShouldScroll(currentEl.scrollHeight > contentMaxHeight)
                }
              }
              update()
              window.addEventListener("resize", update)
              cleanupRef.current = () => window.removeEventListener("resize", update)
            }
            rafIdRef.current = null
          })
          return
        }

        // Reset scroll về đầu khi selectedItem thay đổi
        el.scrollTop = 0

        const update = () => {
          const currentEl = contentRef.current
          if (currentEl) {
            setShouldScroll(currentEl.scrollHeight > contentMaxHeight)
          }
        }
        update()
        window.addEventListener("resize", update)
        cleanupRef.current = () => window.removeEventListener("resize", update)
      }

      // Sử dụng requestAnimationFrame để đảm bảo DOM đã được render
      rafIdRef.current = requestAnimationFrame(checkRef)

      return () => {
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current)
          rafIdRef.current = null
        }
        if (cleanupRef.current) {
          cleanupRef.current()
          cleanupRef.current = null
        }
      }
    }, [selectedItem, isModalOpen, contentMaxHeight])

    useEffect(() => {
      const setMaxH = () => {
        const maxH = window.innerWidth < 640 ? 231 : 420
        setContentMaxHeight(maxH)
      }
      setMaxH()
      window.addEventListener("resize", setMaxH)
      return () => window.removeEventListener("resize", setMaxH)
    }, [])

    return (
      <>
        <div className="relative">
          <Swiper
            modules={[Navigation, Autoplay]}
            navigation={{
              nextEl: ".enroll-swiper-next",
              prevEl: ".enroll-swiper-prev",
            }}
            loop={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }}
            spaceBetween={24}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 2, spaceBetween: 24 },
              1024: { slidesPerView: 4, spaceBetween: 40 },
            }}
          >
            {items &&
              items.map((item, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="rounded-xl w-full sm:gap-4 gap-2 cursor-pointer"
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="w-full sm:h-[270px] h-[343px] rounded-xl overflow-hidden">
                      <Image
                        src={
                          item.img
                            ? typeof item.img === "string"
                              ? `${ADMIN_API_CONFIG.API_URL}${item.img}`
                              : `${ADMIN_API_CONFIG.API_URL}${item.img.url}`
                            : DefaultImage
                        }
                        alt={
                          item.img
                            ? typeof item.img === "string"
                              ? item.name || `Slide ${index + 1}`
                              : item.img.alternativeText || item.name || `Slide ${index + 1}`
                            : item.name || `Slide ${index + 1}`
                        }
                        width={300}
                        height={270}
                        className="w-full h-full object-cover rounded-xl hover:scale-105 transition-all duration-300"
                      />
                    </div>
                    <div className="flex flex-col sm:gap-2 gap-1">
                      <p className="sm:text-[24px] sm:leading-[28px] text-xl leading-[30px] font-semibold text-[#242F3E]">
                        {item.name}
                      </p>
                      <p className="sm:text-base sm:leading-[22px] text-base leading-[27px] font-normal text-[#4F4F4F]">
                        {item.position}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>

          <div className="flex items-center justify-center gap-10 sm:pt-10 pt-6">
            <button
              className="enroll-swiper-prev rounded-full bg-[#EBEDF0] p-3 cursor-pointer"
              aria-label="Previous slide"
            >
              <ChevronLeft size={16} />
            </button>
            <button className="enroll-swiper-next rounded-full bg-[#EBEDF0] p-3 cursor-pointer" aria-label="Next slide">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <Modal
          open={isModalOpen}
          onCancel={handleModalClose}
          footer={null}
          width={789}
          centered
          style={{ borderRadius: "16px", top: !isMobile ? 0 : "10%" }}
        >
          <div className="flex sm:flex-row flex-col sm:gap-10 gap-6 sm:p-10 pt-[56px] pb-6 px-4 rounded-2xl">
            <div className="flex flex-col items-center sm:w-[39%] w-full gap-6">
              <Image
                src={selectedItem?.img ? `${ADMIN_API_CONFIG.API_URL}${selectedItem.img}` : DefaultImage}
                alt={selectedItem?.name ?? "Selected Item"}
                width={300}
                height={270}
                className="w-full h-[270px] object-cover rounded-2xl"
              />
              <button
                className="flex items-center gap-2 px-3 py-2 bg-[#0B9444] text-white text-base leading-[22px] font-semibold rounded-[100px] border border-[#0B9444] shadow-lg shadow-[#0000000D] transition-all duration-300 hover:bg-white hover:text-[#0B9444] hover:border-[#0B9444] group"
                onClick={() => {
                  window.location.href = `mailto:${mailTo}`
                }}
              >
                {t("Drop an Email")}
              </button>
            </div>
            <div className="sm:w-[61%] w-full">
              <p className="text-2xl leading-[28px] font-semibold text-[#0B9444]">{selectedItem?.name}</p>
              <p className="text-base leading-[22px] font-normal text-[#242F3E] pt-3 pb-4">{selectedItem?.position}</p>
              <div className="relative">
                <div
                  ref={contentRef}
                  className={`sm:text-[15px] text-sm leading-[21px] font-light text-[#242F3E] pr-4 ${
                    shouldScroll ? "overflow-y-auto enroll-modal-scroll" : "overflow-y-hidden"
                  }`}
                  style={{ maxHeight: contentMaxHeight, height: contentMaxHeight }}
                  dangerouslySetInnerHTML={{ __html: selectedItem?.description ?? "" }}
                />
                {shouldScroll && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-full w-1 bg-[#6670851F] rounded-full pointer-events-none" />
                )}
              </div>
            </div>
          </div>
        </Modal>
      </>
    )
  },
}
